import PermissionButton from '../../permissions/PermissionButton';
import { usePermissions } from '../../permissions/usePermissions';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Input, Button, Space, Tabs, message, Popconfirm, Drawer, Modal } from 'antd';
import { SearchOutlined, DeleteOutlined, LoadingOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useFetchAllAvoir, useDeleteAvoir } from '../../hooks/avoirHooks';
import AvoirForm from './AvoirForm';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

const AvoirList = ({ onSelectAvoir }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { avoir, loading, error, refetch } = useFetchAllAvoir();
  const { handleDelete, isLoading: isDeleting } = useDeleteAvoir();
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [activeTab, setActiveTab] = useState('notValidated');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  
  const handleAvoirCreated = (avoirId) => {
    setIsDrawerOpen(false);
    onSelectAvoir(avoirId);
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  const handleDeleteAvoir = async (id) => {
    try {
      await handleDelete(id);
      message.success('Avoir deleted successfully');
      refetch();
    } catch (err) {
      message.error(`Failed to delete avoir: ${err.message}`);
    }
  };

  const getDateColumn = () => ({
    title: activeTab === 'validated' ? 'Validation Date' : 'Creation Date',
    key: 'date',
    render: (record) => {
      const date = record.VALIDER ? record.DATEVALID : record.DATE_AVR;
      return dayjs(date).format('DD/MM/YYYY');
    },
    sorter: (a, b) => {
      const dateA = a.VALIDER ? a.DATEVALID : a.DATE_AVR;
      const dateB = b.VALIDER ? b.DATEVALID : b.DATE_AVR;
      return dayjs(dateA).unix() - dayjs(dateB).unix();
    },
  });

  const getColumns = () => [
    {
      title: 'Reference',
      dataIndex: 'REF_AVR',
      key: 'REF_AVR',
      ...getColumnSearchProps('REF_AVR'),
    },
    getDateColumn(),
    {
      title: 'Account',
      dataIndex: 'COMPTE',
      key: 'COMPTE',
      ...getColumnSearchProps('COMPTE'),
    },
    {
      title: 'Client',
      dataIndex: 'CLIENT',
      key: 'CLIENT',
      ...getColumnSearchProps('CLIENT'),
    },
    {
      title: 'Total TTC',
      dataIndex: 'MNT_TTC',
      key: 'MNT_TTC',
      render: value => value?.toFixed(2),
      sorter: (a, b) => a.MNT_TTC - b.MNT_TTC,
    },
    {
      title: 'Remarks',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        activeTab === 'notValidated' && (
          <Popconfirm
            title="Are you sure you want to delete this avoir?"
            onConfirm={() => handleDeleteAvoir(record.REF_AVR)}
            okText="Yes"
            cancelText="No"
          >
            <PermissionButton permissionModule="returns" permissionAction="delete"
              icon={isDeleting ? <LoadingOutlined /> : <DeleteOutlined />}
              danger
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </PermissionButton>
          </Popconfirm>
        )
      ),
      onCell: () => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      }),
    },
  ];

  const getFilteredAvoir = () => {
    switch(activeTab) {
      case 'notValidated':
        return avoir.filter(a => !a.VALIDER);
      case 'validated':
        return avoir.filter(a => a.VALIDER);
      default:
        return avoir;
    }
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    marginTop: "-10px",
  };

  const tableCardStyle = {
    marginTop: "20px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Card style={tableCardStyle}>
      <Modal title="Paramètres des avoirs" open={settingsOpen} onCancel={() => setSettingsOpen(false)} footer={null}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button block onClick={() => { setSettingsOpen(false); navigate('/bulk?type=counter'); }}>Compteur</Button>
          <div>Gérer le compteur Avoir1 (AV1).</div>
          <Button block onClick={() => { setSettingsOpen(false); navigate('/bulk?type=debugging'); }}>Debugging</Button>
          <div>Gérer le compteur Avoir3 (AV3).</div>
        </Space>
      </Modal>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={titleStyle}>Avoir List</h2>
        <Space>
          {(can('returnCounters') || can('counters')) && <Button
            type="default" 
            icon={<SettingOutlined />}
            aria-label="Compteurs avoir"
            title="Compteurs avoir"
            onClick={() => setSettingsOpen(true)}
          />}
          <PermissionButton permissionModule="returns" permissionAction="create"
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsDrawerOpen(true)}
          >
            Create Avoir
          </PermissionButton>
        </Space>
      </Space>

      <Tabs defaultActiveKey="notValidated" onChange={setActiveTab}>
        <TabPane tab="Not Validated" key="notValidated" />
        <TabPane tab="Validated" key="validated" />
      </Tabs>

      <Table
        columns={getColumns()}
        dataSource={getFilteredAvoir()}
        rowKey="REF_AVR"
        pagination={{ pageSize: 10 }}
        onRow={(record) => ({
          onClick: () => onSelectAvoir(record.id),
          style: { cursor: 'pointer' }
        })}
      />

      <Drawer
        title="Create New Avoir"
        width={920}
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <AvoirForm 
          onAvoirCreated={handleAvoirCreated}
        />
      </Drawer>

    </Card>
  );
};

export default AvoirList;
